import { useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import { FlowsheetTab } from './FlowsheetTab';
import { DocumentsTab } from './DocumentsTab';
import { LabsTab } from './LabsTab';
import { TimelineTab } from './TimelineTab';

type TabId = 'flowsheet' | 'documents' | 'labs' | 'timeline';

interface ReferencePanelProps {
  patientId: string;
  pregnancyId: string | null;
  contextViewTemplateId: string | null;
  currentEncounterId?: string;
}

export function ReferencePanel({
  patientId,
  pregnancyId,
  contextViewTemplateId,
  currentEncounterId,
}: ReferencePanelProps) {
  const visibleTabs = useMemo<Array<{ id: TabId; label: string }>>(
    () => [
      { id: 'flowsheet', label: 'Flowsheet' },
      { id: 'documents', label: 'Documents' },
      { id: 'labs', label: 'Labs' },
      { id: 'timeline', label: 'Timeline' },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<TabId>('flowsheet');

  useEffect(() => {
    const isActiveVisible = visibleTabs.some((t) => t.id === activeTab);
    if (!isActiveVisible) setActiveTab(visibleTabs[0].id);
  }, [activeTab, visibleTabs]);

  return (
    <div className="flex h-full flex-col">
      <div
        role="tablist"
        aria-label="Reference panel"
        className="flex gap-1 border-b px-3 py-2"
      >
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`ref-panel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`ref-panel-${activeTab}`}
        aria-label={visibleTabs.find((t) => t.id === activeTab)?.label}
        className="flex-1 overflow-y-auto p-3"
      >
        {activeTab === 'flowsheet' && (
          <FlowsheetTab
            patientId={patientId}
            pregnancyId={pregnancyId}
            contextViewTemplateId={contextViewTemplateId}
            currentEncounterId={currentEncounterId}
          />
        )}
        {activeTab === 'documents' && <DocumentsTab patientId={patientId} />}
        {activeTab === 'labs' && <LabsTab patientId={patientId} />}
        {activeTab === 'timeline' && <TimelineTab patientId={patientId} />}
      </div>
    </div>
  );
}
